import {
  DEFAULT_RAILS_REALM_ID,
  DEFAULT_REALM_NAME,
  RealmTypes,
} from 'RealmModule/constants/realm';
import { Realm } from 'types';
import { Tenant } from 'TenantModule/types';
import pick from 'lodash/pick';
import { SandboxStatusTypes } from 'SandboxModule/constants';
import { TenantStatusTypes } from 'TenantModule/constants';
import { getEnvVar } from '../utils/env';
import { urlContextStore } from './urlContextStore';

export const DEFAULT_TENANT_NAME = getEnvVar('UI_ENV') || '';
export const DEFAULT_TENANT_ID = 1;

export const isParty = getEnvVar('UI_ENV') === 'party';
export const isProd = getEnvVar('UI_ENV') === 'prod';
export const isGovCloud = getEnvVar('UI_ENV') === 'gov';

export enum LocalStorageKeys {
  Realm = 'sb-rails',
  Tenant = 'mosaic-tenant',

  // Storing only the realm id when authenticating temporarily
  LastSelectedRealmId = 'lsrid',
  LastSelectedTenantName = 'lstn',

  // store last trigger dump file request time for cooldown
  LastTriggerDumpFileRequestTime = 'lastTriggerDumpFileRequestTime',
}
/**
 * Builds sandbox baseURL from baseURL and realmId
 * by inserting realmId into the beginning of any API that is supported by sandboxes
 * Format: [protocol]//[realmid].api.[region].[env].mosaicapp.com
 * i.e: https://{realmId}.api.us-east-1.demo.mosaicapp.com - https://mosaic_proddump.api.us-east-1.demo.mosaicapp.com
 */
const _buildUrl = ({ baseURL, realmId }: { baseURL: string; realmId: string }) => {
  const { protocol, host, pathname } = new URL(baseURL);

  // https://mosaicapp.slack.com/archives/C03G4RL6E3A/p1659470362967569?thread_ts=1659468577.776349&cid=C03G4RL6E3A
  // should replace [realmid] in the host with passed realmId
  return `${protocol}//${realmId}.${host}${pathname}`;
};

/** A class for managing api related things (i.e: when a sandbox is selected) */
class ApiClientManager {
  private realm: Realm;
  private tenant: Tenant;
  private readonly defaultRealm: Realm = {
    realm_id: DEFAULT_RAILS_REALM_ID, // Technically its not a sandbox, but an option to revert back to regular demo endpoint
    hosts: {},
    created_at: '',
    archived_at: '',
    team_name: DEFAULT_REALM_NAME,
    is_ready: true,
    tags: [],
    status: SandboxStatusTypes.Initialized,
    created_by: '',
    archived_by: '',
    realm_type: RealmTypes.sandbox,
    tenant: DEFAULT_TENANT_NAME,
    realm_services: [],
  };
  private readonly defaultTenant: Tenant = {
    id: DEFAULT_TENANT_ID,
    isDefault: true,
    archivedAt: '',
    archiver: '',
    createdAt: '',
    creator: '',
    csm: '',
    isArchived: false,
    realms: [],
    status: TenantStatusTypes.Ready,
    tags: [],
    tenantName: DEFAULT_TENANT_NAME,
    tenantMetadata: null,
    primaryRealm: DEFAULT_RAILS_REALM_ID,
  };

  constructor() {
    // ✅ FIX: Wrap localStorage in try-catch to handle private browsing mode and quota exceeded
    let realm: string | null = null;
    let storedTenant: string | null = null;

    try {
      realm = window.localStorage.getItem(LocalStorageKeys.Realm);
      storedTenant = window.localStorage.getItem(LocalStorageKeys.Tenant);
    } catch (error) {
      console.warn('localStorage is not available:', error);
      // Continue with default values
    }

    try {
      this.realm = realm ? JSON.parse(realm) : this.getDefaultRealm();
    } catch (error) {
      console.warn('Failed to parse realm from localStorage:', error);
      this.realm = this.getDefaultRealm();
    }

    try {
      this.tenant = storedTenant ? JSON.parse(storedTenant) : this.defaultTenant;
    } catch (error) {
      console.warn('Failed to parse tenant from localStorage:', error);
      this.tenant = this.defaultTenant;
    }
  }

  reset() {
    this.removeRealm();
    this.removeTenant();
  }
  /**
   * baseUrl is used for building the url when a non-default sandbox is selected
   * defaultUrl is the default url that will be used if sandbox is not selected
   */

  /**
   * Build HTTP headers for tenant/realm context.
   *
   * Priority (for each header independently):
   * 1. Explicitly passed parameters (tenantName, realmId)
   * 2. URL context from urlContextStore (synced from URL params)
   * 3. Auth defaults (user's login tenant/realm)
   *
   * This ensures that when URL has ?tenant=X&realmId=Y, ALL API calls
   * use that context automatically, even from Redux thunks.
   */
  buildUrlHeaders({
    tenantName,
    realmId,
  }: { tenantName?: Tenant['tenantName']; realmId?: Realm['realm_id'] } = {}) {
    // Get URL context (will be undefined if no URL params)
    const urlContext = urlContextStore.getContext();

    // Priority: explicit param > URL context > auth defaults
    const tenantNameToUse =
      tenantName || urlContext.tenant || this.getAuthDefaultTenant()?.tenantName;

    const realmIdToUse = realmId || urlContext.realmId || this.getAuthDefaultRealmId();

    return {
      'x-tenant': tenantNameToUse,
      'x-realm-id': realmIdToUse,
    };
  }

  /* ------------------------------ Rails Sandbox ----------------------------- */
  /**
   * Using sandbox means 1) Sandbox is enabled AND 2) Using a non default sandbox
   */
  getIsUsingNonDefaultRealm() {
    const currentRealm = this.getCurrentRealm();
    const defaultRealm = this.getDefaultRealm();
    const nonDefaultRealm = currentRealm.realm_id !== defaultRealm.realm_id;

    return this.getIsRealmEnabled() && nonDefaultRealm;
  }

  getDefaultRealm() {
    return this.defaultRealm;
  }

  getCurrentRealm() {
    if (process.env.NODE_ENV === 'development') {
      console.warn(
        'ApiClientManager.getCurrentRealm() is deprecated. Use getAuthDefaultRealm() or pass context explicitly via useCurrentContext().',
      );
    }
    return this.realm;
  }

  getCurrentRealmId() {
    if (process.env.NODE_ENV === 'development') {
      console.warn(
        'ApiClientManager.getCurrentRealmId() is deprecated. Use getAuthDefaultRealmId() or pass context explicitly via useCurrentContext().',
      );
    }
    return this.realm.realm_id;
  }

  /**
   * Get the authenticated user's default realm (set at login).
   * Use this for fallback when URL context is not available.
   */
  getAuthDefaultRealm() {
    return this.realm;
  }

  /**
   * Get the authenticated user's default realm ID (set at login).
   * Use this for fallback when URL context is not available.
   */
  getAuthDefaultRealmId() {
    return this.realm.realm_id;
  }

  // If we want to persist realm even closed browser, use localStorage
  setRailsSandbox(realm: Realm) {
    try {
      window.localStorage.setItem(
        LocalStorageKeys.Realm,
        JSON.stringify(pick(realm, ['realm_type', 'realm_id'])),
      );
    } catch (error) {
      console.warn('Failed to save realm to localStorage:', error);
      // Continue without persisting to localStorage
    }
    this.realm = realm;
  }

  removeRealm() {
    try {
      window.localStorage.removeItem(LocalStorageKeys.Realm);
    } catch (error) {
      console.warn('Failed to remove realm from localStorage:', error);
    }
  }

  buildUrl(
    realmId: string,
    isUsingNonDefaultSandbox: boolean,
    baseURL: string,
    defaultUrl: string,
  ) {
    const url = isUsingNonDefaultSandbox
      ? _buildUrl({
          realmId,
          baseURL,
        })
      : defaultUrl;

    return url;
  }

  buildUrlV2({
    service,
    tenantName,
    shouldAppendApi = false,
  }: {
    service: 'integration-server' | 'api';
    tenantName?: Tenant['tenantName'];
    shouldAppendApi?: boolean; // Used when service = api to append "api" at the end of the URL
  }) {
    const environment = getEnvVar('UI_ENV');
    const region = getEnvVar('REGION');
    const { tenantName: globalTenantName } = this.getTenant(); // Globally selected tenant

    // Use globalTenantName if tenantName is not provided
    const tenantNameToUse = tenantName || globalTenantName;

    const formattedUrl = `https://${service}.${tenantNameToUse}.${environment}.${region}.mosaicapp.com`;

    return shouldAppendApi ? `${formattedUrl}/api` : formattedUrl;
  }

  getRailsServerUrl({
    tenantName,
  }: {
    // TenantName used to check if user is super admin on selected realm before redircting
    tenantName?: string;
  } = {}) {
    const override = getEnvVar('RAILS_API_BASE_URL');
    if (override) return override;

    return this.buildUrlV2({
      service: 'api',
      tenantName,
      shouldAppendApi: true,
    });
  }

  buildRailsServerApiConfigs({
    tenantName,
    realmId,
  }: {
    tenantName?: string;
    realmId?: string;
  } = {}) {
    return {
      baseURL: this.getRailsServerUrl({
        tenantName,
      }),
      headers: this.buildUrlHeaders({
        tenantName,
        realmId,
      }),
    };
  }

  buildGalaxyServerApiConfigs({ tenantName, realmId }: { tenantName?: string; realmId?: string }) {
    return {
      headers: this.buildUrlHeaders({
        tenantName,
        realmId,
      }),
    };
  }

  /* -------------------------------------------------------------------------- */

  //
  getGalaxyServerUrl() {
    return getEnvVar('GALAXY_SERVER_API_URL');
  }

  //
  getIntegrationServerUrl({
    tenantName,
  }: {
    tenantName?: string;
  } = {}) {
    return this.buildUrlV2({
      service: 'integration-server',
      tenantName,
    });
  }

  buildIntegrationServerApiConfigs({
    tenantName,
    realmId,
  }: {
    tenantName?: string;
    realmId?: string;
  } = {}) {
    const currentRealmId = this.getCurrentRealmId();
    // these two realms are using the main IS db, not their own realm is db.
    // they wrere created before IS starting supporting realm
    const shouldUseDefaultHeader = currentRealmId === 'eki-1853';

    return {
      baseURL: this.getIntegrationServerUrl({
        tenantName,
      }),
      headers: this.buildUrlHeaders({
        tenantName,
        realmId: shouldUseDefaultHeader ? getEnvVar('UI_ENV') : realmId,
      }),
    };
  }

  getWebappUrl() {
    return this.getWebappUrlForTenant();
  }

  /**
   * Get webapp URL for a specific tenant.
   * If no tenant is provided, uses the globally selected tenant.
   *
   * @param tenantName - Optional tenant name to build URL for
   * @returns The webapp URL for the specified or global tenant
   */
  getWebappUrlForTenant(tenantName?: string) {
    const { tenantName: globalTenantName } = this.getTenant();
    const defaultTenant = this.getDefaultTenant();
    const tenantNameToUse = tenantName || globalTenantName;
    const isNonDefaultTenant = tenantNameToUse !== defaultTenant.tenantName;
    const environment = getEnvVar('UI_ENV');

    if (isNonDefaultTenant) {
      if (isParty) {
        return `https://${tenantNameToUse}.${environment}.mosaicapp.com`;
      }

      // prod
      return `https://${tenantNameToUse}.mosaicapp.com`;
    }

    // Default Tenant
    return getEnvVar('WEB_APP_URL');
  }

  getLoginRepoUrl({ tenantName }: { tenantName: Tenant['tenantName'] }) {
    const defaultTenant = this.getDefaultTenant();
    const isNonDefaultTenant = tenantName !== defaultTenant.tenantName;
    const environment = getEnvVar('UI_ENV');
    const urlSuffix = `mosaicapp.com/login?sourceUrl=${getEnvVar(
      'GALAXY_SOURCE_URL_REDIRECT_AFTER_SUCCESS_AUTH',
    )}`;

    if (isNonDefaultTenant) {
      if (isProd) {
        // https://login-stanleygroup.mosaicapp.com
        return `https://login-${tenantName}.${urlSuffix}`;
      }

      // https://login-release.party.mosaicapp.com
      if (isParty) {
        return `https://login-${tenantName}.${environment}.${urlSuffix}`;
      }
    }

    if (isProd && !isNonDefaultTenant) {
      // https://login.mosaicapp.com
      return `https://login.${urlSuffix}`;
    }

    // party on default tenant
    // https://party-login.mosaicapp.com
    return `https://${environment}-login.${urlSuffix}`;
  }

  /**
   *
   * Refer https://mosaic-ai.atlassian.net/l/cp/8GM6vk3q
   */
  getIsSandboxRealmEnabled() {
    return isParty || isProd;
  }

  /**
   *
   * Refer https://mosaic-ai.atlassian.net/l/cp/8GM6vk3q
   */
  getIsSubscriptionRealmEnabled() {
    return isProd || isParty;
  }

  /**
   *
   * The only flag for checking whether sandbox is enabled on environmen
   * Useful for checking whether to fetch sandboxes or not
   */
  getIsRealmEnabled() {
    return (
      this.getIsSandboxRealmEnabled() || this.getIsSubscriptionRealmEnabled()
      // NOTE: Somehow on prod, fetching sandbox from glx server give 401 (for now)
      // It is reported, once it is fixed, remove this comment
    );
  }

  getIsSubscriptionRealmSelected() {
    const currentRealm = this.getCurrentRealm();

    return currentRealm.realm_type === RealmTypes.subscription;
  }

  getIsSandboxRealmSelected() {
    const currentRealm = this.getCurrentRealm();
    const isSelectedRealmSandbox = currentRealm.realm_type === RealmTypes.sandbox;

    return isSelectedRealmSandbox;
  }

  getTenant() {
    if (process.env.NODE_ENV === 'development') {
      console.warn(
        'ApiClientManager.getTenant() is deprecated. Use getAuthDefaultTenant() or pass context explicitly via useCurrentContext().',
      );
    }
    return this.tenant;
  }

  getTenantId() {
    if (process.env.NODE_ENV === 'development') {
      console.warn(
        'ApiClientManager.getTenantId() is deprecated. Use getAuthDefaultTenant().id or pass context explicitly via useCurrentContext().',
      );
    }
    return this.tenant.id;
  }

  /**
   * Get the authenticated user's default tenant (set at login).
   * Use this for fallback when URL context is not available.
   */
  getAuthDefaultTenant() {
    return this.tenant;
  }

  removeTenant() {
    try {
      window.localStorage.removeItem(LocalStorageKeys.Tenant);
    } catch (error) {
      console.warn('Failed to remove tenant from localStorage:', error);
    }
  }

  getDefaultTenant() {
    return this.defaultTenant;
  }

  setTenant(tenant: Tenant) {
    try {
      window.localStorage.setItem(
        LocalStorageKeys.Tenant,
        JSON.stringify(pick(tenant, ['id', 'tenantName'])),
      );
    } catch (error) {
      console.warn('Failed to save tenant to localStorage:', error);
      // Continue without persisting to localStorage
    }
    this.tenant = tenant; // id and tenantName
  }
}

export default new ApiClientManager();
